import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import Toast, { useToast } from "../components/Toast";
import {
  obtenerOrden, crearOrden, actualizarOrden, siguienteFolio,
} from "../services/ordenesServicioService";
import { buscarClientePorClave } from "../services/clienteService";

const today = () => new Date().toISOString().slice(0, 10);

const EMPTY = {
  FechaEntrada:   today(),
  FechaEntrega:   today(),
  Cliente:        "",
  NombreCliente:  "",
  Contacto:       "",
  Trabajo:        "",
  PrecioEstimado: "",
  Iva:            "",
  Total:          "",
};

const MAX_FOTOS = 3;
const MAX_PX    = 1024; // máximo ancho/alto al comprimir
const JPEG_Q    = 0.75;

function comprimirImagen(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > MAX_PX || height > MAX_PX) {
          if (width > height) { height = Math.round((height * MAX_PX) / width); width = MAX_PX; }
          else                { width  = Math.round((width  * MAX_PX) / height); height = MAX_PX; }
        }
        const canvas = document.createElement("canvas");
        canvas.width  = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", JPEG_Q));
      };
      img.onerror = reject;
      img.src = ev.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function OrdenesServicioFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { toasts, closeToast, success: ok, error: err } = useToast();

  const [form, setForm]       = useState(EMPTY);
  const [folio, setFolio]     = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving]   = useState(false);
  const [readonly, setReadonly] = useState(false);
  const [clienteMsg, setClienteMsg] = useState("");

  // fotos[0..2]: base64 string | null
  const [fotos, setFotos]         = useState([null, null, null]);
  const [fotoLoading, setFotoLoading] = useState([false, false, false]);
  const inputRefs = [useRef(), useRef(), useRef()];

  useEffect(() => {
    if (isEdit) {
      obtenerOrden(id)
        .then(o => {
          setFolio(o.ordenTrabajoId);
          setReadonly(o.status === "C");
          setForm({
            FechaEntrada:   o.fechaEntrada?.slice(0, 10) ?? today(),
            FechaEntrega:   o.fechaEntrega?.slice(0, 10) ?? today(),
            Cliente:        o.cliente,
            NombreCliente:  o.nombreCliente,
            Contacto:       o.contacto,
            Trabajo:        o.trabajo,
            PrecioEstimado: o.precioEstimado ?? "",
            Iva:            o.iva ?? "",
            Total:          o.total ?? "",
          });
          setFotos([o.foto1 ?? null, o.foto2 ?? null, o.foto3 ?? null]);
        })
        .catch(() => err("No se pudo cargar la orden"))
        .finally(() => setLoading(false));
    } else {
      siguienteFolio().then(setFolio).catch(() => {});
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClienteBlur = async (clave) => {
    const c = clave.trim().toUpperCase();
    if (!c) { setClienteMsg(""); return; }
    const empresaId = Number(localStorage.getItem("empresaId") || 1);
    try {
      const cli = await buscarClientePorClave(empresaId, c);
      if (!cli) { setClienteMsg("Cliente no encontrado"); return; }
      setClienteMsg("");
      setForm(f => ({
        ...f,
        Cliente: c,
        NombreCliente: cli.nombre ?? cli.razonSocial ?? cli.nombreCliente ?? "",
        Contacto: f.Contacto || (cli.contacto ?? ""),
      }));
    } catch {
      setClienteMsg("Error al buscar cliente");
    }
  };

  const calcTotales = (precio, ivaPorc) => {
    const p = parseFloat(precio) || 0;
    const ivaMonto = Math.round(p * (parseFloat(ivaPorc) / 100) * 100) / 100;
    return { iva: ivaMonto, total: Math.round((p + ivaMonto) * 100) / 100 };
  };

  const set = (field, val) => setForm(f => {
    const next = { ...f, [field]: val };
    if (field === "PrecioEstimado" || field === "_IvaPorc") {
      const t = calcTotales(
        field === "PrecioEstimado" ? val : next.PrecioEstimado,
        field === "_IvaPorc"       ? val : (next._IvaPorc ?? 16),
      );
      next.Iva   = t.iva   === 0 ? "" : String(t.iva);
      next.Total = t.total === 0 ? "" : String(t.total);
    }
    return next;
  });

  const handleFotoSeleccionada = async (idx, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFotoLoading(prev => { const n = [...prev]; n[idx] = true; return n; });
    try {
      const base64 = await comprimirImagen(file);
      setFotos(prev => { const n = [...prev]; n[idx] = base64; return n; });
    } catch {
      err("No se pudo procesar la foto");
    } finally {
      setFotoLoading(prev => { const n = [...prev]; n[idx] = false; return n; });
      e.target.value = "";
    }
  };

  const eliminarFoto = (idx) => {
    setFotos(prev => { const n = [...prev]; n[idx] = null; return n; });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readonly) return;
    setSaving(true);
    try {
      const dto = {
        FechaEntrada:   form.FechaEntrada,
        FechaEntrega:   form.FechaEntrega,
        Cliente:        form.Cliente,
        NombreCliente:  form.NombreCliente,
        Contacto:       form.Contacto,
        Trabajo:        form.Trabajo,
        PrecioEstimado: form.PrecioEstimado !== "" ? Number(form.PrecioEstimado) : null,
        Iva:            form.Iva            !== "" ? Number(form.Iva)            : null,
        Total:          form.Total          !== "" ? Number(form.Total)          : null,
        Foto1:          fotos[0] ?? "",
        Foto2:          fotos[1] ?? "",
        Foto3:          fotos[2] ?? "",
      };
      if (isEdit) {
        await actualizarOrden(id, dto);
        ok("Orden actualizada");
      } else {
        const res = await crearOrden(dto);
        ok("Orden creada");
        navigate(`/servicios/ordenes/${res.id}/editar`, { replace: true });
        return;
      }
    } catch (ex) {
      err(ex?.response?.data || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 32, color: "var(--clr-text-muted)" }}>Cargando…</div>;

  const fotosTomadas = fotos.filter(Boolean).length;
  const puedeAgregarFoto = fotosTomadas < MAX_FOTOS;

  return (
    <div>
      <Toast toasts={toasts} onClose={closeToast} />
      <PageHeader
        title={isEdit ? `Orden #${folio}` : "Nueva Orden de Servicio"}
        subtitle={readonly ? "Solo lectura — orden cancelada" : "Datos generales"}
      />

      <form onSubmit={handleSubmit} style={{ maxWidth: 700 }}>
        {/* Fechas */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px", marginBottom: 16 }}>
          <Field label="Fecha de Entrada *">
            <input className="lmd-input" type="date" value={form.FechaEntrada} required disabled={readonly}
              onChange={e => set("FechaEntrada", e.target.value)} />
          </Field>
          <Field label="Fecha de Entrega *">
            <input className="lmd-input" type="date" value={form.FechaEntrega} required disabled={readonly}
              onChange={e => set("FechaEntrega", e.target.value)} />
          </Field>
        </div>

        {/* Cliente */}
        <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: "0 20px", marginBottom: 16 }}>
          <Field label="Clave cliente">
            <input className="lmd-input" value={form.Cliente} maxLength={15} disabled={readonly}
              placeholder="CLI001"
              onChange={e => set("Cliente", e.target.value.toUpperCase())}
              onBlur={e => handleClienteBlur(e.target.value)} />
            {clienteMsg && <span style={{ fontSize: 11, color: "var(--clr-danger, #dc3545)" }}>{clienteMsg}</span>}
          </Field>
          <Field label="Nombre del cliente *">
            <input className="lmd-input" value={form.NombreCliente} required maxLength={50} disabled={readonly}
              placeholder="Empresa o persona"
              onChange={e => set("NombreCliente", e.target.value)} />
          </Field>
        </div>

        <Field label="Contacto" style={{ marginBottom: 16 }}>
          <input className="lmd-input" value={form.Contacto} maxLength={50} disabled={readonly}
            placeholder="Nombre del contacto"
            onChange={e => set("Contacto", e.target.value)} />
        </Field>

        <Field label="Descripción del trabajo *" style={{ marginBottom: 16 }}>
          <textarea className="lmd-input" value={form.Trabajo} required maxLength={200} disabled={readonly}
            rows={4} placeholder="Detalle del servicio o reparación a realizar"
            style={{ resize: "vertical" }}
            onChange={e => set("Trabajo", e.target.value)} />
        </Field>

        {/* Precios */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 20px", marginBottom: 24 }}>
          <Field label="Precio estimado">
            <input className="lmd-input" type="number" step="0.01" min="0" value={form.PrecioEstimado}
              disabled={readonly} placeholder="0.00"
              onChange={e => set("PrecioEstimado", e.target.value)} />
          </Field>
          <Field label="IVA">
            <input className="lmd-input" type="number" step="0.01" min="0" value={form.Iva}
              disabled={readonly} placeholder="0.00"
              onChange={e => set("Iva", e.target.value)} />
          </Field>
          <Field label="Total">
            <input className="lmd-input" type="number" step="0.01" min="0" value={form.Total}
              disabled={readonly} placeholder="0.00"
              onChange={e => set("Total", e.target.value)} />
          </Field>
        </div>

        {/* Fotos de evidencia */}
        <div style={{ marginBottom: 24 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginBottom: 10 }}>
            FOTOS DE EVIDENCIA ({fotosTomadas}/{MAX_FOTOS})
          </label>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {fotos.map((foto, idx) => (
              <div key={idx} style={estiloSlot}>
                {foto ? (
                  <>
                    <img
                      src={foto}
                      alt={`Evidencia ${idx + 1}`}
                      style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }}
                    />
                    {!readonly && (
                      <button
                        type="button"
                        onClick={() => eliminarFoto(idx)}
                        style={estiloBorrarFoto}
                        title="Eliminar foto"
                      >
                        ×
                      </button>
                    )}
                  </>
                ) : fotoLoading[idx] ? (
                  <span style={{ color: "var(--clr-text-muted)", fontSize: 12 }}>Procesando…</span>
                ) : !readonly && puedeAgregarFoto ? (
                  <button
                    type="button"
                    onClick={() => inputRefs[idx].current?.click()}
                    style={estiloBotonFoto}
                    title="Tomar o seleccionar foto"
                  >
                    <span style={{ fontSize: 28, lineHeight: 1 }}>📷</span>
                    <span style={{ fontSize: 11, marginTop: 4 }}>Foto {idx + 1}</span>
                  </button>
                ) : (
                  <span style={{ color: "#ccc", fontSize: 11 }}>—</span>
                )}
                {/* input oculto — capture="environment" abre la cámara trasera en móvil */}
                <input
                  ref={inputRefs[idx]}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  style={{ display: "none" }}
                  onChange={e => handleFotoSeleccionada(idx, e)}
                />
              </div>
            ))}
          </div>
          {!readonly && (
            <p style={{ fontSize: 11, color: "var(--clr-text-muted)", marginTop: 6 }}>
              En móvil se abre la cámara directamente. En computadora permite seleccionar imagen del equipo.
            </p>
          )}
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button type="button" className="lmd-btn lmd-btn-outline"
            onClick={() => navigate("/servicios/ordenes")}>
            ← Regresar
          </button>
          {!readonly && (
            <button type="submit" className="lmd-btn lmd-btn-primary" disabled={saving}>
              {saving ? "Guardando…" : isEdit ? "Guardar cambios" : "Crear orden"}
            </button>
          )}
          {isEdit && (
            <button type="button" className="lmd-btn lmd-btn-outline"
              onClick={() => navigate(`/servicios/ordenes/${id}/print`)}>
              Imprimir
            </button>
          )}
          {isEdit && !readonly && (
            <button type="button" className="lmd-btn lmd-btn-outline"
              style={{ marginLeft: "auto" }}
              onClick={() => navigate(`/servicios/ordenes/${id}/firma`)}>
              ✍️ Ir a firma del cliente
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

const estiloSlot = {
  position: "relative",
  width: 110,
  height: 110,
  border: "2px dashed #ddd",
  borderRadius: 8,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  background: "#fafafa",
};

const estiloBotonFoto = {
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  background: "none",
  border: "none",
  cursor: "pointer",
  color: "var(--clr-primary, #0d6efd)",
};

const estiloBorrarFoto = {
  position: "absolute",
  top: 4,
  right: 4,
  width: 22,
  height: 22,
  borderRadius: "50%",
  background: "rgba(0,0,0,0.55)",
  color: "#fff",
  border: "none",
  cursor: "pointer",
  fontSize: 16,
  lineHeight: "20px",
  textAlign: "center",
  padding: 0,
};

function Field({ label, children, style }) {
  return (
    <div style={{ marginBottom: 4, ...style }}>
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginBottom: 4 }}>
        {label}
      </label>
      {children}
    </div>
  );
}
